import React, { useMemo, useCallback, useState, useEffect, useLayoutEffect, useRef } from 'react';
import { observer } from 'mobx-react-lite';

import { markLaunchStep, setLaunchAttr, setLaunchIframeSource } from '../../../services/launch-telemetry';
import { VirtualGameLaunch } from './virtual-game-launch';
import { DefaultGameLaunch } from './default-game-launch';
import { integration } from '../../../services';
import { isOffline } from '../../../helpers';
import { LaunchType } from '../../../enums';
import { rootStore } from '../../../store';
import { useStore } from '../../../hooks';

// A frame with no document after this long is a dead request, not a slow one: the game document's first byte
// comes at p90 0.7 s on a normal network and p90 6.1 s on a slow one. The 09-30 version fired at 10 s on
// `load`, which waits for every subresource, and reloaded games that were loading fine or already running.
const FRAME_RETRY_MS = 15_000;

// The iframe starts on an about:blank that is same-origin with us (the sandbox keeps allow-same-origin, which
// this read depends on), so its location stays readable until a real document replaces it. A cross-origin game
// document, or a browser error page, makes the read throw: that also means something arrived. Only a readable
// 'about:blank' is "nothing yet".
const documentArrived = (frame: HTMLIFrameElement): boolean => {
  try {
    return frame.contentWindow?.location.href !== 'about:blank';
  } catch {
    return true;
  }
};

export const GameLaunch: React.FC = observer(() => {
  const { postMessageStore, gameStore, lobbyStore, freeDropStore, userActivityStore, loaderStore } = useStore();
  const { launchData: { data: launchData } } = gameStore;
  const { launchType } = launchData || {};
  
  const [gameFrameSource, setGameFrameSource] = useState('');
  // The source that has used its one retry. The nonce is derived from it: 1 for that source, 0 for any other,
  // so a changed game starts on a clean URL and re-selecting the same game keeps the `r` it loaded with.
  const [retriedSource, setRetriedSource] = useState('');
  const frameNonce = gameFrameSource && gameFrameSource === retriedSource ? 1 : 0;
  const frameRef = useRef<HTMLIFrameElement | null>(null);

  useEffect(() => {
    if (launchData) {
      const { launchType, freeDropInfo } = launchData;
      // get pre lobby games
      getPreLobbyGames();
      // set free drop data
      freeDropStore.setFreeDropData(freeDropInfo || null);
      // setting integration type
      integration.init(launchType, rootStore);
      integration.current.registerFullscreenChangeListener();
      
      // generating game launch url
      const src = integration.current.generateLaunchUrl(launchData);
      setGameFrameSource(src);
      markLaunchStep('iframeSet');
      userActivityStore.markLoad('iframeSet');
      setLaunchIframeSource(src);
    }
  }, [launchData]);

  // `r` ran on ~15,900 prod launches on 09-30 and games loaded with it. The source is URL.toString() output,
  // so it always has a query and never a fragment, and the string form is exact.
  const frameSource = useMemo(() => (
    frameNonce
      ? `${gameFrameSource}${gameFrameSource.includes('?') ? '&' : '?'}r=${frameNonce}`
      : gameFrameSource
  ), [gameFrameSource, frameNonce]);

  // Cleared in the commit that swaps the iframe element: the removed element can never fire `load` again and
  // the new one cannot before this task ends, so the flag only ever describes the element on screen.
  useLayoutEffect(() => {
    loaderStore.setFrameLoaded(false);
  }, [frameSource]);

  // Retry only a frame whose document never arrived. Decided from component and store state, never from
  // telemetry: markLaunchStep is a no-op after finishLaunch, so telemetry cannot tell a running game that
  // posted GameLoaded before its `load` event from a dead frame. That is what reloaded running games on 09-30.
  useEffect(() => {
    if (!gameFrameSource || frameNonce > 0) return;          // one retry per game
    const timer = setTimeout(() => {
      const frame = frameRef.current;
      if (!frame) return;                                     // error, maintenance, coming soon: nothing to retry
      const alive = loaderStore.frameLoaded
        || loaderStore.gameLoadedTrigger
        || !!gameStore.gameErrorInfo
        || document.hidden
        || isOffline()
        || documentArrived(frame);
      if (alive) return;
      markLaunchStep('frameRetry');                           // side step, not on GAME_PATH; re-arms the watchdog
      setLaunchAttr('launch.frameRetry.reason', 'no_document');
      setRetriedSource(gameFrameSource);
    }, FRAME_RETRY_MS);
    return () => clearTimeout(timer);
  }, [gameFrameSource, frameNonce]);

  const getPreLobbyGames = useCallback(() => {
    if (launchData?.hasPreLobby) {
      lobbyStore.getPreLobbyGames({
        category: launchData.category,
        partnerId: launchData.partnerId,
        gameId: launchData.gameId,
      });
    }
  }, [launchData]);

  const refCallback = useCallback((element: HTMLIFrameElement | null) => {
    frameRef.current = element;
    postMessageStore.setIframeElement(element);
    userActivityStore.attachEventListeners(element?.contentWindow ?? null);
  }, []);

  const games = useMemo<Partial<Record<LaunchType, React.FC>>>(() => ({
    [LaunchType.Pascal]: () => <DefaultGameLaunch refCallback={refCallback} gameFrameSource={frameSource} frameNonce={frameNonce} />,
    [LaunchType.Virtual]: () => <VirtualGameLaunch refCallback={refCallback} gameFrameSource={frameSource} frameNonce={frameNonce} />
  }), [frameSource, frameNonce, refCallback]);

  if (!launchType) return null;

  const GameComponent = games[launchType];

  return GameComponent
    ? <GameComponent />
    : <DefaultGameLaunch refCallback={refCallback} gameFrameSource={frameSource} frameNonce={frameNonce} />;
});
